import { collectionGroup, onSnapshot, getDocs, limit, orderBy, query, startAfter } from "firebase/firestore";
import { dbLOA } from "../../../../backend/firebaseConfig";

const THRESHOLD = 14;
const getTime = (value) => value?.toDate?.()?.getTime?.() || (typeof value === "number" ? value : new Date(value || 0).getTime());
const counts = (item) => {
  if (item?.counts && Object.values(item.counts).some((value) => Number(value) > 0)) return item.counts;
  return (item?.extractionJson?.answers || item?.answers || []).reduce((result, answer) => {
    const status = String(answer?.status || "").toLowerCase();
    result[status === "found" ? "found" : status === "partial" ? "partial" : "missing"] += 1;
    return result;
  }, { found: 0, partial: 0, missing: 0 });
};

export const fetchProCedingMonthlyChargeRecords = async (month) => {
  const all = [];
  let cursor = null;
  do {
    const constraints = [orderBy("createdAt", "desc"), limit(500)];
    if (cursor) constraints.push(startAfter(cursor));
    const snapshot = await getDocs(query(collectionGroup(dbLOA, "Extractions"), ...constraints));
    all.push(...snapshot.docs);
    cursor = snapshot.docs[snapshot.docs.length - 1] || null;
    if (snapshot.docs.length < 500) break;
  } while (cursor);

  return mapProCedingCharges(all, month);
};

export const subscribeProCedingChargeRecords = (onData, onError) =>
  onSnapshot(query(collectionGroup(dbLOA, "Extractions"), orderBy("createdAt", "desc")),
    (snapshot) => onData(mapProCedingCharges(snapshot.docs)), onError);

const normaliseEmail = (value) => String(value || "").trim().toLowerCase();
const detail = (item, key, question) => item?.summary?.[key] ||
  item?.extractionJson?.summary?.[key] ||
  (item?.extractionJson?.answers || item?.answers || []).find(
    (answer) => normaliseEmail(answer.question) === normaliseEmail(question),
  )?.answer || "";

export const mapProCedingCharges = (all, month) => {
  const rows = all.flatMap((docSnap) => {
    const extraction = docSnap.data() || {};
    const adviserEmail = normaliseEmail(extraction.ownerEmail) ||
      normaliseEmail(docSnap.ref?.parent?.parent?.id) ||
      normaliseEmail(extraction.adviserEmail || extraction.userEmail || extraction.createdBy);
    if (!adviserEmail || adviserEmail.endsWith("@rosemountfs.co.uk")) return [];
    const dateMs = getTime(extraction.createdAtMs || extraction.createdAt || extraction.updatedAtMs || extraction.updatedAt);
    const date = new Date(dateMs);
    if (!dateMs || Number.isNaN(date.getTime())) return [];
    const schemes = Array.isArray(extraction.schemes) && extraction.schemes.length ? extraction.schemes : [extraction];
    if (extraction.status !== "completed" && !schemes.some((scheme) =>
      scheme.extractionJson || Array.isArray(scheme.answers) ||
      Object.values(scheme.counts || {}).some((value) => Number(value) > 0) ||
      (scheme === extraction && scheme.summary))) return [];
    return schemes.flatMap((scheme, index) => {
      const schemeCounts = counts(scheme);
      const found = Number(schemeCounts.found || 0);
      const amount = found > THRESHOLD ? 1 : 0;
      return [{
        id: `pro-ceding-${adviserEmail}-${docSnap.id}-${index}`,
        extractionId: docSnap.id, schemeNumber: index + 1, schemeCount: schemes.length,
        adviserEmail, adviser: adviserEmail,
        createdBy: extraction.createdBy || extraction.userEmail || adviserEmail,
        client: detail(scheme, "clientName", "Client's Name") || detail(extraction, "clientName", "Client's Name"),
        providerName: detail(scheme, "providerName", "Pension Provider Name") || detail(extraction, "providerName", "Pension Provider Name"),
        policyNumber: detail(scheme, "policyNumber", "Policy Number or Plan Number") || detail(extraction, "policyNumber", "Policy Number or Plan Number"),
        counts: schemeCounts, chargeType: "ProCeding", description: "Scheme analysis",
        date: date.toISOString(), amountCharged: amount, expectedAmount: amount,
        chargeable: amount > 0, free: false, sourcePath: "/pro-ceding-statements",
      }];
    });
  });
  // Apply the lifetime allowance before selecting a month, including below-threshold analyses.
  const seen = new Map();
  return rows.sort((a, b) => a.date.localeCompare(b.date)).map((row) => {
    const count = seen.get(row.adviserEmail) || 0;
    seen.set(row.adviserEmail, count + 1);
    return count < 10 ? { ...row, amountCharged: 0, expectedAmount: 0, free: true, chargeable: false } : row;
  }).filter((row) => !month || row.date.slice(0, 7) === month);
};
